<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import { Gallery } from 'flowbite-svelte';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import { animate, inView } from '@motionone/dom';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import { reveal } from 'svelte-reveal';

  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let componentsCount = 0;
  let pagesCount = 0;
  let yearsCount = 0;
  let adoptionRate = 0;
  let userSatisfaction = 0;
  let timeSavings = 0;

  // Adjusted gallery image arrays to include three images per column
  const galleryColumn1 = [
    { 
      src: 'https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fqcare.fb6af2a9.png&w=3840&q=80',
      alt: 'QCare Atlas - Main dashboard overview'
    },
    { 
      src: 'https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fgsa-thumb.61593c9f.png&w=3840&q=80',
      alt: 'Gesundheitsatlas - Disease mapping interface'
    },
    { 
      src: 'https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fqcare-insights-1.da91361a.png&w=3840&q=75',
      alt: 'QCare Atlas - Healthcare insights dashboard'
    }
  ];

  function handleTagClick(tag: string) {
    console.log(`Filter by: ${tag}`);
    // Add filtering logic here
  }

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 85, 2000, (val) => adoptionRate = val);
          animateValue(0, 90, 1000, (val) => userSatisfaction = val);
          animateValue(0, 1200, 1500, (val) => timeSavings = val);
          animateValue(0, 250, 2000, (val) => componentsCount = val);
          animateValue(0, 80, 1000, (val) => pagesCount = val);
          animateValue(0, 2, 1500, (val) => yearsCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();

    return () => {
      metricsObserver.disconnect();
    };
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="AOK Design System"
    client="Scientific institute of the AOK Medical Insurance"
    clientLogo="/logos/AOK.png"
    industry="Healthcare"
    product="A design system and interactive dashboards"
    heroMedia={{
      type: 'image',
      src: 'https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fqcare-multiple-landscape.1d824f98.jpg&w=3840&q=80'
    }}
    metrics={[
      { value: 85, label: "Adoption Rate" },
      { value: 90, label: "User Satisfaction" },
      { value: 1200, label: "Time Savings (hours)" }
    ]}
    roles={[
      "Healthcare UX",
      "Data Visualization",
      "Accessibility"
    ]}
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Large Text Block -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90">
        A comprehensive design system serving as a tool to generate research and dashboards that are unified in style and accessibility.
      </p>
    </div>


    <ChallengeSolution
    challengeText="Developing a unified design system for the Scientific Institute's fast-paced research projects is crucial. It must enhance public health and support decision-makers with reliable, real-time information across platforms."
    solutionText="The AOK Design System standardizes design elements, ensuring consistency and efficiency. It empowers researchers and decision-makers with timely data, improving public health outcomes and fostering informed decisions."
  />

    <!-- Interface Gallery -->
    <div class="w-full mb-16">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8 animate-on-view" use:reveal={{ y: 10 }}>Interface Gallery</h2>
      <div class="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4 px-8">
        <!-- Column 1 -->
        <div class="space-y-4">
          {#each galleryColumn1 as image, i}
            <div class="overflow-hidden rounded-lg animate-on-view" data-delay="0">
              <img src={image.src} alt={image.alt} class="w-full h-auto" />
              <p class="text-sm text-gray-600 mt-2">
                {#if i === 0}
                  QCare – Quality assurance and measurement in German care homes, first of its kind
                {:else if i === 1}
                  Health Atlas Germany, comparing and improving health based on several diseases and KPIs
                {:else if i === 2}
                  Continuous prescription of psychotropics as a key indicator of quality assurance
                {/if}
              </p>
            </div>
          {/each}
        </div>
      </div>
    </div>

    <!-- Media and Press Section -->
    <div class="w-full mb-16">
      <h2 class="text-2xl font-light animate-on-view mt-8 mb-8 px-8" use:reveal={{ y: 10 }}>Media & Press</h2>
      <FullWidthImage
      src="/projects/aok-design-system/press.png"
      alt="AOK Design System Mockup"
      caption="Several projects built upon the design system have been featured in numerous media outlets, highlighting its impact on healthcare innovation and design consistency."
    />
    </div>

    <!-- Mock Image Section -->
    <div class="w-full mb-16">
      <div class="animate-on-view">
        <h2 class="text-2xl font-light mt-8 mb-8 px-8" use:reveal={{ y: 10 }}>Accelerating Impact</h2>
      </div>
      <FullWidthImage
        src="/projects/aok-design-system/mock.png"
        alt="AOK Design System Mockup"
        caption="The AOK Design System accelerates decision-making processes, enhances the communication of research findings, and supports the acquisition of funding."
      />
    </div>

    <!-- Key Features Section -->
    <div class="w-full mb-16 animate-on-view">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8" use:reveal={{ y: 10 }}>Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"></path></svg>'
          title="Component Library"
          description="250+ reusable components built with accessibility and flexibility in mind"
          delay={0}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>'
          title="Documentation"
          description="Comprehensive guidelines and usage examples for developers and designers"
          delay={0.2}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path></svg>'
          title="Design Tokens"
          description="Centralized design tokens for colors, typography, spacing, and more"
          delay={0.4}
        />
      </div>
    </div>
  </div>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Made to Measure", url: "/projects/made-to-measure" }}
    nextProject={{ title: "Project Soli", url: "/projects/project-soli" }}
  />

  <Footer />
</div> 